import { useMemo } from 'react'
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useB2bBillingActions } from '../../hooks/useB2bBillingActions'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { formatPeriodEnd, formatOrgSubscriptionStatus } from '../../helpers/billingDisplay.helper'
import { ENTERPRISE_CONTACT_PATH } from '../../../packs/constants/enterpriseContact.constants'
import {
  ACCOUNT_ORG_BILLING_COPY,
  MANUAL_BILLING_PAYMENT_METHOD_LABELS,
} from '../../constants/accountOrgBilling.constants'
import { getAccountBillingTabStyles } from '../AccountBillingTab/AccountBillingTab.styles'
import type { Theme } from '../../../theme/getTheme'
import type { B2bAccountDto } from '../../types/b2bAccount.types'

interface AccountOrgBillingSectionProps {
  accountId: string | null
  account: B2bAccountDto
  theme: Theme
  stripeReturnSyncing?: boolean
}

export function AccountOrgBillingSection({
  accountId,
  account,
  theme,
  stripeReturnSyncing = false,
}: AccountOrgBillingSectionProps) {
  const copy = useAccountAdminCopy()
  const styles = useMemo(() => getAccountBillingTabStyles(theme), [theme])
  const router = useRouter()
  const { openPortal, busy: busyOrg, stripeConfigured } = useB2bBillingActions(
    accountId,
    account,
  )

  const hasOrganization = accountId != null
  const hasOrgSubscription = Boolean(account.stripeSubscriptionStatus?.trim())
  const isOrgFree = account.planTier === 'FREE' || account.planTier === 'STARTER'
  const isManualBilling = account.billingProvider === 'MANUAL'
  const isStripeCustom = account.billingProvider === 'STRIPE_CUSTOM'
  const isStripeCustomPaid =
    isStripeCustom &&
    (hasOrgSubscription || Boolean(account.stripePaymentIntentId?.trim()))

  const orgPortalAvailable =
    hasOrganization && account.stripeBillingPortalAvailable === true && hasOrgSubscription

  const showSubscriptionBlock = hasOrgSubscription
  const showManualBlock = isManualBilling
  const showPortal = !isStripeCustom && orgPortalAvailable
  const showStripeCustomUnpaidHint = isStripeCustom && !isStripeCustomPaid
  const showUnconfigured =
    !stripeConfigured && !isStripeCustom && !isOrgFree && hasOrgSubscription
  const showEmptyState =
    !showSubscriptionBlock &&
    !showManualBlock &&
    !showPortal &&
    !showStripeCustomUnpaidHint &&
    !showUnconfigured

  return (
    <View style={styles.billingSubsection}>
      <Text style={styles.subsectionTitle}>{ACCOUNT_ORG_BILLING_COPY.SECTION_TITLE}</Text>

      {showEmptyState ? (
        <Text style={styles.orgBillingDisclosure}>{ACCOUNT_ORG_BILLING_COPY.EMPTY_INTRO}</Text>
      ) : null}

      {showStripeCustomUnpaidHint ? (
        <Text style={styles.orgBillingDisclosure}>
          {ACCOUNT_ORG_BILLING_COPY.STRIPE_CUSTOM_UNPAID_HINT}
        </Text>
      ) : null}

      {showManualBlock ? (
        <>
          {account.manualBillingSummary ? (
            <View style={styles.manualBillingCard}>
              <Text style={styles.subsectionTitle}>
                {ACCOUNT_ORG_BILLING_COPY.MANUAL_PAYMENT_TITLE}
              </Text>
              <View style={styles.manualSummaryRow}>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Amount paid</Text>
                  <Text style={styles.statusValue}>
                    {account.manualBillingSummary.currency}{' '}
                    {account.manualBillingSummary.totalAmountPaid}
                  </Text>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Method</Text>
                  <Text style={styles.statusValue}>
                    {MANUAL_BILLING_PAYMENT_METHOD_LABELS[
                      account.manualBillingSummary.paymentMethod as keyof typeof MANUAL_BILLING_PAYMENT_METHOD_LABELS
                    ] ?? account.manualBillingSummary.paymentMethod}
                  </Text>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Seats</Text>
                  <Text style={styles.statusValue}>{account.maxSeats}</Text>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Paid on</Text>
                  <Text style={styles.statusValue}>
                    {formatPeriodEnd(account.manualBillingSummary.latestPaidAt)}
                  </Text>
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.manualPendingBadge}>
              <Text style={styles.manualPendingText}>Payment pending</Text>
            </View>
          )}
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Contact us"
            onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
            style={({ pressed, hovered }) => [
              styles.manualContactLink,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.manualContactLinkPressed,
              hovered && Platform.OS === 'web' && styles.manualContactLinkHover,
            ]}
          >
            <Text style={styles.manualContactLinkText}>Contact us</Text>
          </Pressable>
        </>
      ) : null}

      {showSubscriptionBlock ? (
        <View style={styles.statusSection}>
          <View style={styles.statusItem}>
            <Text style={styles.statusLabel}>{copy.billing.SUBSCRIPTION_STATUS}</Text>
            <Text style={styles.statusValue}>
              {formatOrgSubscriptionStatus(account.stripeSubscriptionStatus, copy.billing)}
            </Text>
          </View>
          <View style={styles.statusItem}>
            <Text style={styles.statusLabel}>{copy.billing.PERIOD_END}</Text>
            <Text style={styles.statusValue}>
              {formatPeriodEnd(account.stripeCurrentPeriodEnd)}
            </Text>
          </View>
        </View>
      ) : null}

      {showUnconfigured ? (
        <View style={styles.unconfiguredBanner}>
          <Text style={styles.unconfiguredText}>{copy.billing.STRIPE_NOT_CONFIGURED}</Text>
        </View>
      ) : null}

      {showPortal ? (
        <View style={styles.actionsRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={copy.billing.MANAGE_PORTAL}
            disabled={busyOrg || stripeReturnSyncing}
            onPress={() => void openPortal()}
            style={({ pressed, hovered }) => [
              styles.portalBtn,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.portalBtnPressed,
              hovered && Platform.OS === 'web' && styles.portalBtnHover,
              (busyOrg || stripeReturnSyncing) && styles.portalBtnDisabled,
            ]}
          >
            {busyOrg ? (
              <ActivityIndicator size="small" color={theme.black} />
            ) : (
              <Text style={styles.portalBtnText}>{copy.billing.MANAGE_PORTAL}</Text>
            )}
          </Pressable>
        </View>
      ) : null}
    </View>
  )
}
